import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { toast } from '@/hooks/useToast';
import { ApiError } from '@/lib/api/client';
import { decideProposal, type DoctorPreference } from './memoryApi';

/**
 * The inline proposal card (§4.4 point 1): "You removed X from N of your
 * last 20 notes. Leave it out automatically from now on?" — shown after
 * approving a note, at most once per session (the caller enforces that by
 * only calling GET /proposals/next once and holding the result in state,
 * not by anything here). `statement` already reads naturally for both a
 * removed- and an added-section pattern (utils/aiStyleLearning.js builds
 * "Leaves out the…"/"Adds a…" so this card never needs to know which).
 */
export function ProposalCard({ proposal, onDone }: { proposal: DoctorPreference; onDone: () => void }) {
  const [busy, setBusy] = useState<'approve' | 'not_now' | 'never' | null>(null);

  const decide = async (decision: 'approve' | 'not_now' | 'never') => {
    setBusy(decision);
    try {
      await decideProposal(proposal.id, decision);
      if (decision === 'approve') {
        toast({ variant: 'success', title: 'Remembered ✓', description: proposal.statement, duration: 10000 });
      } else if (decision === 'not_now') {
        toast({ title: 'Snoozed', description: `We'll ask again after more of your notes.` });
      } else {
        toast({ title: 'Got it — won\'t suggest this again' });
      }
      onDone();
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not save your answer', description: e instanceof ApiError ? e.message : undefined });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-teal/30 bg-teal/5 p-3">
      <div className="flex items-start gap-2 text-sm">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-teal" aria-hidden="true" />
        <span>
          Noticed in {proposal.evidence_count ?? 'several'} of your recent documents: <strong>{proposal.statement}</strong>. Do this automatically from now on?
        </span>
      </div>
      <div className="flex gap-2">
        <Button type="button" size="sm" disabled={!!busy} onClick={() => decide('approve')}>
          {busy === 'approve' ? '…' : '✓ Yes, remember'}
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={!!busy} onClick={() => decide('not_now')}>
          {busy === 'not_now' ? '…' : 'Not now'}
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={!!busy} onClick={() => decide('never')}>
          {busy === 'never' ? '…' : 'Never suggest this'}
        </Button>
      </div>
    </div>
  );
}
